import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { callingsApi } from '../../services/api';
import Modal from '../shared/Modal';
import toast from 'react-hot-toast';

const WARDS = [
  'Ala Árvore Grande',
  'Ala Barcelona',
  'Ala Votorantim',
  'Ala Rio Acima',
  'Ala São Roque',
  'Ramo Mairinque',
  'Ramo Piedade',
] as const;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function NewCallingModal({ isOpen, onClose }: Props) {
  const [personName, setPersonName] = useState('');
  const [ward, setWard] = useState('');
  const [type, setType] = useState<'chamado' | 'desobrigacao'>('chamado');
  const [recommendedBy, setRecommendedBy] = useState('');
  const [callingName, setCallingName] = useState('');
  const [sustainScope, setSustainScope] = useState<'ala' | 'estaca'>('ala');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      callingsApi.create({
        person_name: personName,
        ward,
        type,
        recommended_by: recommendedBy,
        calling_name: callingName,
        sustain_scope: sustainScope,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(`${type === 'chamado' ? 'Chamado' : 'Desobrigação'} criado!`);
      onClose();
      reset();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const reset = () => {
    setPersonName('');
    setWard('');
    setType('chamado');
    setRecommendedBy('');
    setCallingName('');
    setSustainScope('ala');
  };

  const valid = personName.trim() && ward && recommendedBy.trim() && callingName.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Novo Chamado / Desobrigação"
      footer={
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">
            Cancelar
          </button>
          <button
            type="submit"
            form="new-calling-form"
            disabled={!valid || mutation.isPending}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Salvando...' : 'Criar'}
          </button>
        </div>
      }
    >
      <form
        id="new-calling-form"
        onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
        className="space-y-4"
      >
        {/* Type selector */}
        <div>
          <label className="label">Tipo</label>
          <div className="grid grid-cols-2 gap-2">
            {(['chamado', 'desobrigacao'] as const).map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                  type === t
                    ? 'bg-brand-500 border-brand-500 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {t === 'chamado' ? 'Chamado' : 'Desobrigação'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label">
            Nome da pessoa <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={personName}
            onChange={e => setPersonName(e.target.value)}
            className="input"
            placeholder="Nome completo"
            required
            autoFocus
          />
        </div>

        <div>
          <label className="label">
            Ala / Ramo <span className="text-red-500">*</span>
          </label>
          <select
            value={ward}
            onChange={e => setWard(e.target.value)}
            className="input"
            required
          >
            <option value="">Selecione...</option>
            {WARDS.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>

        <div>
          <label className="label">
            Recomendado por <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={recommendedBy}
            onChange={e => setRecommendedBy(e.target.value)}
            className="input"
            placeholder="Nome do bispo ou líder"
            required
          />
        </div>

        <div>
          <label className="label">
            Cargo <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={callingName}
            onChange={e => setCallingName(e.target.value)}
            className="input"
            placeholder="Ex: Presidente da SRS"
            required
          />
        </div>

        <div>
          <label className="label">
            Escopo do apoio <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {([
              ['ala', 'Somente na ala'],
              ['estaca', 'Na estaca inteira'],
            ] as const).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setSustainScope(v)}
                className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                  sustainScope === v
                    ? 'bg-brand-500 border-brand-500 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
